import React, { forwardRef, useEffect, useImperativeHandle, useState } from 'react';
import {
  Box,
  Button,
  FormControl,
  FormControlLabel,
  Radio,
  RadioGroup,
  Typography,
} from '@mui/material';
import {
  LaborSalesTaxApplicability,
  LaborSalesTaxRuleDTO,
  LaborSalesTaxWorkJurisdictionIdentifier,
} from '../../common/CommonInterfaces';
import { ToolTipCommonComponent } from '../../common/CommonComponents';
import { WorkySelectComponent } from '../../shared-components/worky-select-component/WorkySelectComponent';
import {
  CLIENT_FACING_LABOR_SALES_TAX_TOOLTIPS,
  LABOR_SALES_TAX_CHARGE_TAX_LABEL,
  LABOR_SALES_TAX_DO_NOT_CHARGE_TAX_LABEL,
  LABOR_SALES_TAX_JURISDICTION_LABEL,
  LABOR_SALES_TAX_RATE_PERCENT_LABEL,
  LABOR_SALES_TAX_USE_DEFAULT_LABEL,
} from '../agency-data/AgencyLaborSalesTaxClientTooltips';

const LABOR_SALES_TAX_JURISDICTIONS: readonly LaborSalesTaxWorkJurisdictionIdentifier[] = [
  'ONTARIO',
  'BRITISH_COLUMBIA',
  'ALBERTA',
];

type JurisdictionOption = {
  readonly jurisdictionKey: LaborSalesTaxWorkJurisdictionIdentifier;
  readonly jurisdictionLabel: string;
};

const JURISDICTION_OPTIONS: readonly JurisdictionOption[] = [
  { jurisdictionKey: 'ONTARIO', jurisdictionLabel: 'Ontario (HST)' },
  { jurisdictionKey: 'BRITISH_COLUMBIA', jurisdictionLabel: 'British Columbia (GST)' },
  { jurisdictionKey: 'ALBERTA', jurisdictionLabel: 'Alberta (GST)' },
];

type TaxRatePercentOption = {
  readonly ratePercent: number;
  readonly ratePercentLabel: string;
};

const TAX_RATE_PERCENT_OPTIONS: readonly TaxRatePercentOption[] = Array.from(
  { length: 101 },
  (_unused, ratePercent: number): TaxRatePercentOption => ({
    ratePercent,
    ratePercentLabel: `${ratePercent}%`,
  })
);

const resolveTaxRatePercentOption = (
  customRatePercent: string
): TaxRatePercentOption | null => {
  if (customRatePercent.trim() === '') {
    return null;
  }
  const parsedPercent: number = Number(customRatePercent);
  if (!Number.isFinite(parsedPercent)) {
    return null;
  }
  const roundedPercent: number = Math.round(parsedPercent);
  if (roundedPercent < 0 || roundedPercent > 100) {
    return null;
  }
  return TAX_RATE_PERCENT_OPTIONS[roundedPercent] ?? null;
};

type JurisdictionEditorRow = {
  readonly applicability: LaborSalesTaxApplicability | 'INHERIT';
  readonly customRatePercent: string;
};

const emptyRow = (): JurisdictionEditorRow => ({
  applicability: 'INHERIT',
  customRatePercent: '',
});

const rulesToEditorState = (
  rules: readonly LaborSalesTaxRuleDTO[] | null | undefined
): Record<LaborSalesTaxWorkJurisdictionIdentifier, JurisdictionEditorRow> => {
  const state: Record<LaborSalesTaxWorkJurisdictionIdentifier, JurisdictionEditorRow> = {
    ONTARIO: emptyRow(),
    BRITISH_COLUMBIA: emptyRow(),
    ALBERTA: emptyRow(),
  };
  for (const rule of rules ?? []) {
    if (!LABOR_SALES_TAX_JURISDICTIONS.includes(rule.workJurisdictionIdentifier)) {
      continue;
    }
    state[rule.workJurisdictionIdentifier] = {
      applicability: rule.laborSalesTaxApplicability,
      customRatePercent:
        rule.taxRate === null || rule.taxRate === undefined
          ? ''
          : String(Math.round(rule.taxRate * 100)),
    };
  }
  return state;
};

const jurisdictionLabelForKey = (
  jurisdiction: LaborSalesTaxWorkJurisdictionIdentifier
): string => {
  const option: JurisdictionOption | undefined = JURISDICTION_OPTIONS.find(
    (jurisdictionOption: JurisdictionOption): boolean =>
      jurisdictionOption.jurisdictionKey === jurisdiction
  );
  return option?.jurisdictionLabel ?? jurisdiction;
};

const editorStateToRules = (
  state: Record<LaborSalesTaxWorkJurisdictionIdentifier, JurisdictionEditorRow>
): LaborSalesTaxRuleDTO[] => {
  const rules: LaborSalesTaxRuleDTO[] = [];
  for (const jurisdiction of LABOR_SALES_TAX_JURISDICTIONS) {
    const row = state[jurisdiction];
    if (row.applicability === 'INHERIT') {
      continue;
    }
    let taxRate: number | null = null;
    if (row.applicability === 'TAX_APPLICABLE_ON_FULL_AMOUNT') {
      if (row.customRatePercent.trim() === '') {
        throw new Error(
          `Select a sales tax rate (%) for ${jurisdictionLabelForKey(jurisdiction)} when Charge tax is selected.`
        );
      }
      const percent = Number(row.customRatePercent);
      if (!Number.isFinite(percent) || percent < 0 || percent > 100 || !Number.isInteger(percent)) {
        throw new Error(
          `Select a sales tax rate from 0% to 100% for ${jurisdictionLabelForKey(jurisdiction)}.`
        );
      }
      taxRate = percent / 100;
    }
    rules.push({
      workJurisdictionIdentifier: jurisdiction,
      laborSalesTaxApplicability: row.applicability,
      taxRate,
    });
  }
  return rules;
};

type LaborSalesTaxRulesEditorHandle = {
  readonly buildRules: () => LaborSalesTaxRuleDTO[];
};

type LaborSalesTaxRulesEditorProps = {
  readonly initialRules: readonly LaborSalesTaxRuleDTO[] | null | undefined;
  readonly helperNote?: string;
  readonly isSaving: boolean;
  readonly hideActionButtons?: boolean;
  readonly onValidationErrorChange?: (validationError: string | null) => void;
  readonly onCancel?: () => void;
  readonly onSave?: (rules: LaborSalesTaxRuleDTO[]) => Promise<void>;
};

const LaborSalesTaxRulesEditor = forwardRef<
  LaborSalesTaxRulesEditorHandle,
  LaborSalesTaxRulesEditorProps
>(({
  initialRules,
  helperNote,
  isSaving,
  hideActionButtons = false,
  onValidationErrorChange,
  onCancel,
  onSave,
}, ref): React.ReactElement => {
  const [editorState, setEditorState] = useState(() => rulesToEditorState(initialRules));
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [selectedJurisdictionOption, setSelectedJurisdictionOption] = useState<JurisdictionOption>(
    JURISDICTION_OPTIONS[0]
  );

  const selectedRow = editorState[selectedJurisdictionOption.jurisdictionKey];
  const isChargeTaxSelected: boolean =
    selectedRow.applicability === 'TAX_APPLICABLE_ON_FULL_AMOUNT';
  const selectedTaxRatePercentOption: TaxRatePercentOption | null =
    resolveTaxRatePercentOption(selectedRow.customRatePercent);
  const isChargeTaxRateMissingOnSelected: boolean =
    isChargeTaxSelected && selectedTaxRatePercentOption === null;
  const hasAnyChargeTaxWithoutRate: boolean = LABOR_SALES_TAX_JURISDICTIONS.some(
    (jurisdiction: LaborSalesTaxWorkJurisdictionIdentifier): boolean => {
      const row: JurisdictionEditorRow = editorState[jurisdiction];
      return (
        row.applicability === 'TAX_APPLICABLE_ON_FULL_AMOUNT'
        && resolveTaxRatePercentOption(row.customRatePercent) === null
      );
    }
  );
  const isSaveDisabled: boolean = isSaving || hasAnyChargeTaxWithoutRate;

  const reportValidationError = (validationError: unknown): string => {
    const validationMessage: string =
      validationError instanceof Error ? validationError.message : 'Failed to save tax rules';
    setErrorMessage(validationMessage);
    return validationMessage;
  };

  useImperativeHandle(ref, (): LaborSalesTaxRulesEditorHandle => ({
    buildRules: (): LaborSalesTaxRuleDTO[] => {
      try {
        const laborSalesTaxRules: LaborSalesTaxRuleDTO[] = editorStateToRules(editorState);
        setErrorMessage(null);
        return laborSalesTaxRules;
      } catch (validationError) {
        reportValidationError(validationError);
        throw validationError;
      }
    },
  }), [editorState]);

  useEffect((): void => {
    if (onValidationErrorChange == null) {
      return;
    }
    try {
      editorStateToRules(editorState);
      onValidationErrorChange(null);
    } catch (validationError) {
      const validationMessage: string =
        validationError instanceof Error ? validationError.message : 'Failed to save tax rules';
      onValidationErrorChange(validationMessage);
    }
  }, [editorState, onValidationErrorChange]);

  const handleSave = async (): Promise<void> => {
    if (onSave == null) {
      return;
    }
    try {
      setErrorMessage(null);
      const rules = editorStateToRules(editorState);
      await onSave(rules);
    } catch (error) {
      reportValidationError(error);
    }
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, minWidth: 320 }}>
      {helperNote ? (
        <Typography variant="body2" color="text.secondary">
          {helperNote}
        </Typography>
      ) : null}
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75 }}>
        <ToolTipCommonComponent
          toolTipText={CLIENT_FACING_LABOR_SALES_TAX_TOOLTIPS.jurisdiction}
        >
          <Typography variant="subtitle2" component="span">
            {LABOR_SALES_TAX_JURISDICTION_LABEL}
          </Typography>
        </ToolTipCommonComponent>
        <WorkySelectComponent<JurisdictionOption>
          options={[...JURISDICTION_OPTIONS]}
          value={[selectedJurisdictionOption]}
          onChange={(nextOptions): void => {
            setSelectedJurisdictionOption(nextOptions[0] ?? JURISDICTION_OPTIONS[0]);
          }}
          getLabel={(jurisdictionOption) => jurisdictionOption.jurisdictionLabel}
          getValue={(jurisdictionOption) => jurisdictionOption.jurisdictionKey}
          placeholder={LABOR_SALES_TAX_JURISDICTION_LABEL}
          enableSearch={false}
          minSelections={1}
          maxSelections={1}
        />
      </Box>
      <FormControl>
        <Typography variant="subtitle2" sx={{ mb: 1 }}>
          Tax for {selectedJurisdictionOption.jurisdictionLabel}
        </Typography>
        <RadioGroup
          value={selectedRow.applicability}
          onChange={(event) => {
            const nextApplicability = event.target.value as JurisdictionEditorRow['applicability'];
            setEditorState((prev) => ({
              ...prev,
              [selectedJurisdictionOption.jurisdictionKey]: {
                ...prev[selectedJurisdictionOption.jurisdictionKey],
                applicability: nextApplicability,
              },
            }));
            setErrorMessage(null);
          }}
        >
          <FormControlLabel
            value="INHERIT"
            control={<Radio />}
            label={
              <ToolTipCommonComponent
                toolTipText={CLIENT_FACING_LABOR_SALES_TAX_TOOLTIPS.useDefault}
              >
                <span>{LABOR_SALES_TAX_USE_DEFAULT_LABEL}</span>
              </ToolTipCommonComponent>
            }
          />
          <FormControlLabel
            value="TAX_APPLICABLE_ON_FULL_AMOUNT"
            control={<Radio />}
            label={
              <ToolTipCommonComponent
                toolTipText={CLIENT_FACING_LABOR_SALES_TAX_TOOLTIPS.chargeTax}
              >
                <span>{LABOR_SALES_TAX_CHARGE_TAX_LABEL}</span>
              </ToolTipCommonComponent>
            }
          />
          <FormControlLabel
            value="NO_TAX_AT_ALL"
            control={<Radio />}
            label={
              <ToolTipCommonComponent
                toolTipText={CLIENT_FACING_LABOR_SALES_TAX_TOOLTIPS.doNotChargeTax}
              >
                <span>{LABOR_SALES_TAX_DO_NOT_CHARGE_TAX_LABEL}</span>
              </ToolTipCommonComponent>
            }
          />
        </RadioGroup>
      </FormControl>
      {isChargeTaxSelected ? (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75 }}>
          <ToolTipCommonComponent
            toolTipText={CLIENT_FACING_LABOR_SALES_TAX_TOOLTIPS.salesTaxRatePercent}
          >
            <Typography variant="subtitle2" component="span">
              {LABOR_SALES_TAX_RATE_PERCENT_LABEL} *
            </Typography>
          </ToolTipCommonComponent>
          <WorkySelectComponent<TaxRatePercentOption>
            options={[...TAX_RATE_PERCENT_OPTIONS]}
            value={selectedTaxRatePercentOption !== null ? [selectedTaxRatePercentOption] : []}
            onChange={(nextOptions): void => {
              const nextRateOption: TaxRatePercentOption | undefined = nextOptions[0];
              setEditorState((prev) => ({
                ...prev,
                [selectedJurisdictionOption.jurisdictionKey]: {
                  ...prev[selectedJurisdictionOption.jurisdictionKey],
                  customRatePercent:
                    nextRateOption !== undefined ? String(nextRateOption.ratePercent) : '',
                },
              }));
              setErrorMessage(null);
            }}
            getLabel={(taxRatePercentOption) => taxRatePercentOption.ratePercentLabel}
            getValue={(taxRatePercentOption) => String(taxRatePercentOption.ratePercent)}
            placeholder="Select rate (0%–100%)"
            enableSearch
            minSelections={0}
            maxSelections={1}
          />
          {isChargeTaxRateMissingOnSelected ? (
            <Typography variant="caption" color="error">
              Select a sales tax rate when Charge tax is selected.
            </Typography>
          ) : null}
        </Box>
      ) : null}
      {errorMessage ? (
        <Typography variant="body2" color="error">
          {errorMessage}
        </Typography>
      ) : null}
      {hideActionButtons ? null : (
        <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
          <Button onClick={(): void => { onCancel?.(); }} disabled={isSaving}>
            Cancel
          </Button>
          <Button variant="contained" onClick={handleSave} disabled={isSaveDisabled}>
            {isSaving ? 'Saving…' : 'Save'}
          </Button>
        </Box>
      )}
    </Box>
  );
});

LaborSalesTaxRulesEditor.displayName = 'LaborSalesTaxRulesEditor';

export {
  LaborSalesTaxRulesEditor,
  LaborSalesTaxRulesEditorHandle,
  LaborSalesTaxRulesEditorProps,
};
